import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { isNil, sum } from 'lodash';
import { Subscription } from 'rxjs';
import { DisplayMapStoreMunicipality } from '@app/shared/models/municipality/Municipality';
import { MunicipalityManagementConfigurationStore } from '@app/municipality-management/services/municipality-management-configuration.store';
import { MunicipalityManagementService } from '@app/municipality-management/services/municipality-management.service';
import { AuthService, CurrentUser } from '@app/shared/services/auth.service';
import { LocalStorageService } from '@app/shared/services/storage/local-storage.service';
import { NotificationDialogComponent } from '@app/shared/dialog/notification-dialog/notification-dialog.component';
import { ContinueEditingService } from '@app/shared/services/continue-editing.service';
import { CloseDialogResult } from '@app/shared/models/dialog/continue-editing-dialog.model';

interface SearchFieldConfig {
  searchFieldPlaceholder: string;
  floatLabel: string;
  appearance: string;
}

@Component({
  selector: 'app-municipality-selection',
  templateUrl: './municipality-selection.component.html',
  styleUrls: ['./municipality-selection.component.scss'],
  providers: [MunicipalityManagementConfigurationStore],
  encapsulation: ViewEncapsulation.None,
})
export class MunicipalitySelectionComponent implements OnInit, OnChanges, OnDestroy {
  @Input() isExpandedStyle = false;
  @Input() municipalities!: DisplayMapStoreMunicipality[];
  @Input() selectedMunicipality!: DisplayMapStoreMunicipality | string;
  @Input() selectedToggleOption: any;
  @Input() displaySearch = true;
  @Input() showNavigationButton = true;
  @Input() showCheckBox = false;
  @Input() searchFieldConfig: SearchFieldConfig = {
    searchFieldPlaceholder: 'Gemeindesuche',
    floatLabel: 'never',
    appearance: 'fill',
  };
  @Input() searchableByKey: boolean = false;
  @Output() municipalityChangeEvent = new EventEmitter<DisplayMapStoreMunicipality>();
  @Output() municipalitiesChangeEvent = new EventEmitter<DisplayMapStoreMunicipality[]>();
  @Output() displayMunicipalities = new EventEmitter<string>();

  searchText = '';
  filteredMunicipalities: DisplayMapStoreMunicipality[] = [];
  selectedMultipleMunicipalities: DisplayMapStoreMunicipality[] = [];
  user: CurrentUser | null = null;
  @ViewChild(CdkVirtualScrollViewport) virtualScroll: CdkVirtualScrollViewport | undefined;
  virtualScrollMaxHeight = 205;
  virtualScrollHeight = this.virtualScrollMaxHeight;
  showDropdown = false;
  private _subs$ = new Subscription();

  constructor(
    public authService: AuthService,
    private _localStorageService: LocalStorageService,
    private _configStore: MunicipalityManagementConfigurationStore,
    private _service: MunicipalityManagementService,
    private _matDialog: MatDialog,
    private _translateService: TranslateService,
    private continueEditingService: ContinueEditingService
  ) {}

  ngOnInit(): void {
    this._localStorageService.resetMeterDataPagination();
    this.computeVirtualScrollHeight();
    if (this.showCheckBox) {
      this.toggleDropdown();
    }
  }

  ngOnDestroy(): void {
    this.toggleDropdown();
    this._subs$.unsubscribe();
  }

  toggleDropdown(): void {
    this.showDropdown = !this.showDropdown;

    if (!this.showDropdown && this.showCheckBox) {
      this.setMultipleMunicipalities(this.selectedMultipleMunicipalities);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['municipalities']) {
      this.municipalities.forEach((municipality: DisplayMapStoreMunicipality) => {
        const municipalityKey = !!municipality.key ? `(${municipality.key})` : '';
        municipality.displayName = this.authService.isMunicipalityWorker ? municipality.name : `${municipality.name} ${municipalityKey}`;
        this.MunicipalitiesToArray(municipality);
      });
      this.municipalities.sort((a: DisplayMapStoreMunicipality, b: DisplayMapStoreMunicipality) => a.name.localeCompare(b.name));
      this.assignFilteredMunicipalities(this.municipalities);
    }

    if (changes['selectedMunicipality']) {
      this.searchText = typeof this.selectedMunicipality === 'string' ? this.selectedMunicipality : this.selectedMunicipality.name;
      if (!this.showCheckBox && typeof this.selectedMunicipality === 'string') {
        this.municipalities.forEach((muni) => (muni.isSelected = `${muni.name} ${muni.key}` === this.selectedMunicipality));
      }
    }
  }

  selectMunicipality(municipality: DisplayMapStoreMunicipality): void {
    if (this._configStore.hasNoOrphanedMunicipality) {
      municipality.isSelected = !municipality.isSelected;
      this.handleMunicipalitySelection(municipality);
    } else if (!isNil(this._configStore.hasNoOrphanedMunicipality)) {
      this._matDialog
        .open(NotificationDialogComponent, {
          data: {
            descriptionText: this._translateService.instant('module.shared.municipalitySelection.orphanedMunicipalityPromptText'),
            confirmText: this._translateService.instant('module.shared.municipalitySelection.orphanedMunicipalityPromptButtonText'),
          },
          panelClass: ['-md'],
        })
        .afterClosed();
    } else {
      municipality.isSelected = !municipality.isSelected;
      this.nilOrphanedMunicipality(municipality);
    }
  }

  public async confirmSelectMunicipality(municipality: DisplayMapStoreMunicipality): Promise<void> {
    const dialogRef = await this.continueEditingService.openContinueEditingDialog();
    if (dialogRef !== CloseDialogResult.continueEditing) {
      this.selectMunicipality(municipality);
    }
  }

  private handleMunicipalitySelection(municipality: DisplayMapStoreMunicipality): void {
    this.showCheckBox ? this.MunicipalitiesToArray(municipality) : this.setSelectedMunicipality(municipality);
  }

  private nilOrphanedMunicipality(municipality: DisplayMapStoreMunicipality): void {
    this._configStore.canNavigate && this._configStore.isFormValid
      ? this.handleMunicipalitySelection(municipality)
      : this._subs$.add(this._service.saveChanges(true).subscribe((res: boolean | unknown) => (res ? this.handleMunicipalitySelection(municipality) : null)));
  }

  private setSelectedMunicipality(municipality: DisplayMapStoreMunicipality): void {
    this.selectedMunicipality = this.stringifyArray(municipality);
    this.displayMunicipalities.emit(this.selectedMunicipality);
    this.municipalityChangeEvent.emit(municipality);
  }

  private setMultipleMunicipalities(selectedMunicipalities: DisplayMapStoreMunicipality[]): void {
    this.selectedMunicipality = this.stringifyArray(selectedMunicipalities);
    this.displayMunicipalities.emit(this.selectedMunicipality);
    this.municipalitiesChangeEvent.emit(selectedMunicipalities);
  }

  private MunicipalitiesToArray(municipality: DisplayMapStoreMunicipality): void {
    if (!municipality) {
      return;
    }
    const index = this.selectedMultipleMunicipalities.findIndex((m) => m.key === municipality.key);
    if (index !== -1 && municipality.isSelected === false) {
      this.selectedMultipleMunicipalities.splice(index, 1);
    } else if (municipality.isSelected === true) {
      this.selectedMultipleMunicipalities.push(municipality);
    }
    this.selectedMultipleMunicipalities.sort((a: DisplayMapStoreMunicipality, b: DisplayMapStoreMunicipality) => a.name.localeCompare(b.name));
  }

  onMunicipalitySearchChange(searchTerm: KeyboardEvent) {
    if (!isNil(searchTerm)) {
      const term = (searchTerm.target as HTMLInputElement)?.value ?? '';
      this.filteredMunicipalities = Array.from(this.municipalities).filter((item: DisplayMapStoreMunicipality) => {
        if (this.authService.isAdminAreaAndMunicipalityWorker || isNil(item.key)) {
          return item.name.toLowerCase().indexOf(term.toLowerCase()) === 0;
        } else
          return this.searchableByKey
            ? item.name.toLowerCase().indexOf(term.toLowerCase()) === 0 || item.key.toLowerCase().indexOf(term.toLowerCase()) === 0
            : `${item.name} (${item.key})`.toLowerCase().indexOf(term.toLowerCase()) === 0;
      });

      this.computeVirtualScrollHeight();

      if (this.virtualScroll) {
        this.virtualScroll.scrollToIndex(0);
      }
    }
  }

  assignFilteredMunicipalities(municipalities: DisplayMapStoreMunicipality[]): void {
    this.filteredMunicipalities = Array.from(municipalities);
  }

  trackByFn(_: number, item: any) {
    return item.guid;
  }

  computeVirtualScrollHeight(): void {
    const maxTextLengthPerRow = 28;

    const lengthData = this.filteredMunicipalities?.map((m: DisplayMapStoreMunicipality) => {
      const length = m.displayName?.length ?? 0;
      const rowOccupied = Math.ceil(length / maxTextLengthPerRow);
      return (rowOccupied + 1) * 20;
    });

    const totalHeight = sum(lengthData);
    this.virtualScrollHeight = totalHeight <= this.virtualScrollMaxHeight ? totalHeight : this.virtualScrollMaxHeight;
  }

  private stringifyArray(municipality: DisplayMapStoreMunicipality | DisplayMapStoreMunicipality[]): string {
    let result = '';

    if (Array.isArray(municipality)) {
      const mappedArray = municipality.map((m: any) => {
        return typeof m === 'string' ? m : `${m.name} (${m.key})`;
      });
      result = mappedArray.join(', ');
      if (result.length > 60) {
        result = result.slice(0, 57) + '...';
      }
    } else {
      result = `${municipality.displayName}`;
    }
    return result;
  }
}
